import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
// import HttpBackend from 'i18next-http-backend';

//languages files
//EN
import enGeneral from './en/General.json';
import enCommon from './en/Common.json';
import enLanding from './en/Landing.json';
import enLayout from './en/Layout.json';
import enNotFound from './en/NotFound.json';
import enAuthentication from './en/Authentication.json';
import enGrid from './en/Grid.json';
import enNotifications from './en/Notifications.json';
import enAccount from './en/Account.json';
import enInventory from './en/Inventory.json';
import enReports from './en/Reports.json';
import enWidgets from './en/Widgets.json';
import enChannels from './en/Channels.json';
import enIntegrations from './en/Integrations.json';
import enPublications from './en/Publications.json';
import enWarehouses from './en/Warehouses.json';
import enTour from './en/Tour.json';
import enError from './en/Error.json';
import enDashboard from './en/Dashboard.json';
import enOrders from './en/Orders.json';
import enAccounting from './en/Accounting.json';
import enSubscription from './en/Subscription.json';
// ES
import esGeneral from './es/General.json';
import esCommon from './es/Common.json';
import esLanding from './es/Landing.json';
import esLayout from './es/Layout.json';
import esNotFound from './es/NotFound.json';
import esAuthentication from './es/Authentication.json';
import esGrid from './es/Grid.json';
import esNotifications from './es/Notifications.json';
import esAccount from './es/Account.json';
import esInventory from './es/Inventory.json';
import esReports from './es/Reports.json';
import esWidgets from './es/Widgets.json';
import esChannels from './es/Channels.json';
import esIntegrations from './es/Integrations.json';
import esPublications from './es/Publications.json';
import esWarehouses from './es/Warehouses.json';
import esTour from './es/Tour.json';
import esError from './es/Error.json';
import esDashboard from './es/Dashboard.json';
import esOrders from './es/Orders.json';
import esAccounting from './es/Accounting.json';
import esSubscription from './es/Subscription.json';
import enInteractions from './en/Interactions.json';
import esInteractions from './es/Interactions.json';
import enRules from './en/Rules.json';
import esRules from './es/Rules.json';
import enErrors from './en/Errors.json';
import esErrors from './es/Errors.json';
import enBatchProcesses from './en/BatchProcesses.json';
import esBatchProcesses from './es/BatchProcesses.json';
import enAutomation from './en/Automation.json';
import esAutomation from './es/Automation.json';
import enUserSettings from './en/UserSettings.json';
import esUserSettings from './es/UserSettings.json';
import enPriceList from './en/PriceList.json';
import esPriceList from './es/PriceList.json';
import enInvoicing from './en/Invoicing.json';
import esInvoicing from './es/Invoicing.json';
import enLogistics from './en/Logistics.json';
import esLogistics from './es/Logistics.json';
import enMessaging from './en/Messaging.json';
import esMessaging from './es/Messaging.json';
import enOnboarding from './en/Onboarding.json';
import esOnboarding from './es/Onboarding.json';
import enSalesChannelSetup from './en/salesChannelSetup.json';
import esSalesChannelSetup from './es/salesChannelSetup.json';
import enSizeGuides from './en/sizeGuides.json';
import esSizeGuides from './es/sizeGuides.json';
import enOperations from './en/Operations.json';
import esOperations from './es/Operations.json';
// TV1-2158 (S7) — the procurement suppliers namespace.
import enSuppliers from './en/Suppliers.json';
import esSuppliers from './es/Suppliers.json';

// Define shared namespaces that should be available to all other namespaces
const SHARED_NAMESPACES = ['errors'] as const;

// Define regular namespaces
const NAMESPACES = [
  'general',
  // Cross-domain UI chrome shared by more than one domain (TV1-2294: the mobile
  // list toolbar / filter sheet / selection bar). Registering the namespace here
  // is NOT optional — a namespace that is imported but never listed in
  // `NAMESPACES` + `resources` silently resolves every key to its inline
  // default, which looks exactly like a successful translation.
  'common',
  'layout',
  'notFound',
  'landing',
  'authentication',
  'grid',
  'notifications',
  'account',
  'inventory',
  'reports',
  'widgets',
  'channels',
  'integrations',
  'publications',
  'warehouses',
  'tour',
  'error', // Note: This is different from 'errors' - keep for backward compatibility
  'dashboard',
  'orders',
  'accounting',
  'interactions',
  'subscription',
  'rules',
  'batchProcesses',
  'automation',
  'userSettings',
  'priceList',
  'Invoicing',
  'Logistics',
  'Messaging',
  'onboarding',
  'salesChannelSetup',
  'sizeGuides',
  'operations',
  'suppliers'
] as const;

// Combine all namespaces
const ALL_NAMESPACES = [ ...NAMESPACES, ...SHARED_NAMESPACES ];

i18n
  //   .use(HttpBackend) // Load translations from backend files
  .use( LanguageDetector ) // Detect the user's language
  .use( initReactI18next ) // Pass the i18n instance to react-i18next.
  .init({
    /**
     * Spanish is the product's default, not English.
     *
     * `fallbackLng` was `'en'`, so anyone whose browser reported a language the
     * app has no bundle for — or no language at all — landed on English. Teselly
     * is a Rioplatense product: `es` is the language the copy is authored in
     * (every `t( key, 'default' )` fallback string in the codebase is Spanish),
     * and `en` is the translation. Falling back to the translation rather than to
     * the source was backwards.
     *
     * `lng` is deliberately left unset so `LanguageDetector` still wins: a user
     * whose browser says `en` gets English, and a returning user's saved choice
     * (the detector's localStorage/cookie caches) is still honoured. This only
     * changes where the chain ENDS.
     */
    fallbackLng: 'es',
    debug: false, // Enable debug mode for development
    interpolation: {
      escapeValue: false // React already does escaping
    },
    resources: {
      en: {
        general: enGeneral,
        common: enCommon,
        landing: enLanding,
        layout: enLayout,
        notFound: enNotFound,
        authentication: enAuthentication,
        grid: enGrid,
        notifications: enNotifications,
        account: enAccount,
        inventory: enInventory,
        reports: enReports,
        widgets: enWidgets,
        channels: enChannels,
        integrations: enIntegrations,
        publications: enPublications,
        warehouses: enWarehouses,
        suppliers: enSuppliers,
        tour: enTour,
        error: enError,
        dashboard: enDashboard,
        orders: enOrders,
        accounting: enAccounting,
        interactions: enInteractions,
        subscription: enSubscription,
        rules: enRules,
        errors: enErrors,
        batchProcesses: enBatchProcesses,
        automation: enAutomation,
        userSettings: enUserSettings,
        priceList: enPriceList,
        Invoicing: enInvoicing,
        Logistics: enLogistics,
        Messaging: enMessaging,
        onboarding: enOnboarding,
        salesChannelSetup: enSalesChannelSetup,
        sizeGuides: enSizeGuides,
        operations: enOperations
      },
      es: {
        general: esGeneral,
        common: esCommon,
        landing: esLanding,
        layout: esLayout,
        notFound: esNotFound,
        authentication: esAuthentication,
        grid: esGrid,
        notifications: esNotifications,
        account: esAccount,
        inventory: esInventory,
        reports: esReports,
        widgets: esWidgets,
        channels: esChannels,
        integrations: esIntegrations,
        publications: esPublications,
        warehouses: esWarehouses,
        suppliers: esSuppliers,
        tour: esTour,
        error: esError,
        dashboard: esDashboard,
        orders: esOrders,
        accounting: esAccounting,
        interactions: esInteractions,
        subscription: esSubscription,
        rules: esRules,
        errors: esErrors,
        batchProcesses: esBatchProcesses,
        automation: esAutomation,
        userSettings: esUserSettings,
        priceList: esPriceList,
        Invoicing: esInvoicing,
        Logistics: esLogistics,
        Messaging: esMessaging,
        onboarding: esOnboarding,
        salesChannelSetup: esSalesChannelSetup,
        sizeGuides: esSizeGuides,
        operations: esOperations
      }
    },
    ns: ALL_NAMESPACES, // Namespaces that you use in your app
    defaultNS: 'general', // Default namespace to use if not specified
    fallbackNS: SHARED_NAMESPACES // Makes shared namespaces available to all
    // supportedLngs: ['en', 'es'] // List of available languages
    // backend: {
    //   loadPath: '/locales/{{lng}}/{{ns}}.json', // Path to translation files
    // },
  });

export default i18n;
